<script setup lang="ts">
import { z } from 'zod'

definePageMeta({ layout: 'auth', middleware: ['tenant', 'guest'] })

const { t } = useI18n()
const localePath = useLocalePath()
const auth = useAuth()

const schema = z.object({
  identifier: z.string().email()
})

type Schema = {
  identifier: string
}

const state = reactive<Schema>({
  identifier: ''
})

const error = ref<string | null>(null)
const sent = ref(false)

const onSubmit = async () => {
  error.value = null

  try {
    const parsed = schema.parse(state)
    await auth.forgotPassword({ email: parsed.identifier })
    sent.value = true
  } catch (e) {
    if (e instanceof z.ZodError) {
      error.value = t('auth.invalid_email_forgot')
      return
    }

    error.value = e instanceof Error ? e.message : t('auth.error_forgot_password')
  }
}
</script>

<template>
  <section class="flex min-h-screen w-full">
    <div class="relative hidden w-2/3 items-center justify-center p-10 lg:flex">
      <img
        src="/images/login-avatar.png"
        alt="Forgot password illustration"
        class="max-h-[500px] w-auto"
      >
    </div>

    <div class="flex w-full flex-col justify-center px-6 py-12 lg:w-1/3 lg:px-12 ui-bg-elevated">
      <div class="mx-auto w-full max-w-sm">
        <h4 class="text-2xl font-semibold">
          {{ t('auth.forgot_password_title') }}
        </h4>
        <p class="mt-1 text-sm ui-text-subtle">
          {{ t('auth.forgot_password_help') }}
        </p>

        <UAlert
          v-if="error"
          color="error"
          variant="soft"
          :description="error"
          class="mt-4"
        />
        <UAlert
          v-if="sent"
          color="success"
          variant="soft"
          :description="t('auth.forgot_password_sent')"
          class="mt-4"
        />

        <form
          class="mt-6 space-y-4"
          @submit.prevent="onSubmit"
        >
          <UFormField
            label="Email"
            for="identifier"
          >
            <UInput
              id="identifier"
              v-model="state.identifier"
              :placeholder="t('auth.email')"
              type="email"
              autofocus
              class="w-full"
            />
          </UFormField>

          <UButton
            type="submit"
            :loading="auth.isLoading.value"
            class="w-full justify-center"
          >
            {{ t('auth.send_reset_link') }}
          </UButton>
        </form>

        <p class="mt-5 text-center text-sm ui-text-subtle">
          <NuxtLink
            :to="localePath('/login')"
            class="font-medium text-primary hover:underline"
          >
            {{ t('auth.back_to_login') }}
          </NuxtLink>
        </p>
      </div>
    </div>
  </section>
</template>
